"use client"

import type { GetEventsResponse } from "@/modules/events/api/getEvents"
import type { SimpleSelectOption } from "@/modules/layouts/components/MultiSelect/MultiSelect"
import type { FormEventHandler, FunctionComponent, KeyboardEvent } from "react"

import { useSearchParams } from "next/navigation"
import { useRef } from "react"

import { Checkbox } from "@jobteaser/spark/components/Checkbox"
import { Search } from "@jobteaser/spark/components/Icons/Search"
import { MultiSelect } from "@jobteaser/spark/components/MultiSelect"
import { Select } from "@jobteaser/spark/components/Select"
import { TextInput } from "@jobteaser/spark/components/TextInput"

import { getOptionsAndValuesFromFilters } from "@/modules/eventSearch/helpers/getOptionsAndValuesFromFilters"
import { useEventsFilters } from "@/modules/eventSearch/hooks/useEventsFilters"
import { useLayoutContext } from "@/modules/landingSearch/components/Layout/LayoutContextProvider"

import { SecondaryFilters } from "./SecondaryFilters"

import styles from "@/modules/landingSearch/components/Filters/Filters.module.css"

type FiltersProps = {
  clearIconAriaLabel: string
  exclusiveLabel: string
  filters: GetEventsResponse["meta"]["filters"]
  isWwwCareerCenter: boolean
  queryPlaceholder: string
  secondaryFiltersButtonLabel: string
}

export const Filters: FunctionComponent<FiltersProps> = ({
  clearIconAriaLabel,
  exclusiveLabel,
  filters,
  isWwwCareerCenter,
  queryPlaceholder,
  secondaryFiltersButtonLabel,
}) => {
  const { navigateWithUpdatedQueryParams } = useEventsFilters()
  const searchParams = useSearchParams()
  const formRef = useRef<HTMLFormElement>(null)
  const { isSearchLoading } = useLayoutContext()
  const { options: kindsOptions, title: kindsTitle } = getOptionsAndValuesFromFilters({
    field: "kinds",
    filters,
  })
  const {
    options: dateOptions,
    title: dateTitle,
    values: dateValues,
  } = getOptionsAndValuesFromFilters({
    field: "date",
    filters,
  })
  const isExclusiveFilterDisplayed = !isWwwCareerCenter

  function submitForm(): void {
    if (!formRef.current) {
      return
    }

    const formData = new FormData(formRef.current)

    navigateWithUpdatedQueryParams({
      date: (formData.get("date") as string) || null,
      exclusive: (formData.get("exclusive") as string) === "true" ? "true" : "false",
      query: (formData.get("query") as string) || null,
    })
  }

  const onSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault()
    submitForm()
  }

  const handleInputKeydown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      submitForm()
    }
  }

  const onFieldUpdate: FormEventHandler<HTMLElement> = () => {
    submitForm()
  }

  const onFieldChange = (field: string, selectedValues: SimpleSelectOption[]): void => {
    navigateWithUpdatedQueryParams({
      [field]: selectedValues.length ? selectedValues.map(({ value }) => value) : null,
    })
  }

  const onMultiSelectChange =
    (fieldName: string) =>
    (selectedValues: string[]): void => {
      navigateWithUpdatedQueryParams({
        [fieldName]: selectedValues.length ? selectedValues : null,
      })
    }

  return (
    <form className={styles.main} onSubmit={onSubmit} ref={formRef}>
      <div className={styles.mainBar}>
        <TextInput
          className={styles.primaryFilter}
          hasClearIcon
          clearIconAriaLabel={clearIconAriaLabel}
          id="query"
          inputClassName={styles.primaryFilterFieldInput}
          leftIcon={Search}
          name="query"
          onBlur={onFieldUpdate}
          onKeyDown={handleInputKeydown}
          placeholder={queryPlaceholder}
          readOnly={isSearchLoading}
          value={searchParams.get("query") || ""}
        />
        <MultiSelect
          className={styles.primaryFilter}
          clearIconLabel={clearIconAriaLabel}
          defaultValue={searchParams.getAll("kinds")}
          emptyContent=""
          hideLabel
          id="kinds"
          label={kindsTitle}
          multipleSelectedOptionsTextFormatter={String}
          name="kinds"
          onSelect={onMultiSelectChange("kinds")}
          options={kindsOptions.map(option => ({ ...option, type: "option" }))}
          placeholder={kindsTitle}
          readOnly={isSearchLoading}
        />
        <Select
          className={styles.primaryFilter}
          defaultValue={dateValues[0]?.value || ""}
          disabled={isSearchLoading}
          emptyContent=""
          fieldClassName={styles.primaryFilterField}
          hideLabel
          id="date"
          name="date"
          label={dateTitle}
          placeholder={dateTitle}
          onSelect={selectedValue => onFieldChange("date", [{ label: "", value: selectedValue }])}
          options={[
            { label: " ", type: "option", value: "" },
            ...dateOptions.map(({ count, ...props }) => ({
              ...props,
              secondaryLabel: `(${count})`,
              type: "option" as const,
            })),
          ]}
        />
      </div>
      <div className={styles.secondaryBar}>
        {isExclusiveFilterDisplayed && (
          <Checkbox
            checked={searchParams.get("exclusive") === "true"}
            className={styles.secondaryFilter}
            disabled={isSearchLoading}
            name="exclusive"
            onBackground="dark"
            onChange={onFieldUpdate}
            value="true"
          >
            {exclusiveLabel}
          </Checkbox>
        )}
        <SecondaryFilters filters={filters} triggerButtonlabel={secondaryFiltersButtonLabel} />
      </div>
    </form>
  )
}
